<script lang="ts">
  import LoadingSpinner from '../../ui/LoadingSpinner.svelte';
  import type { DropdownOption } from './types';

  interface Props {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companyName: string;
    abn: string;
    abnWarning: string;
    industryType: string;
    companySize: string;
    referralSource: string;
    isSubmitting: boolean;
    isAuthenticated: boolean;
    industryTypes: DropdownOption[];
    companySizes: DropdownOption[];
    referralSources: DropdownOption[];
    onSubmit: (e: Event) => void;
    onABNInput: (e: Event) => void;
    onFieldChange: (field: string, value: string) => void;
  }

  let {
    firstName,
    lastName,
    email,
    phone,
    companyName,
    abn,
    abnWarning,
    industryType,
    companySize,
    referralSource,
    isSubmitting,
    isAuthenticated,
    industryTypes,
    companySizes,
    referralSources,
    onSubmit,
    onABNInput,
    onFieldChange,
  }: Props = $props();
</script>

<form class="signup-form" onsubmit={onSubmit}>
  <div class="form-section">
    <h3>Contact Information</h3>
    <div class="form-grid">
      <div class="form-group">
        <label for="firstName">First Name *</label>
        <input
          type="text"
          id="firstName"
          value={firstName}
          oninput={(e) => onFieldChange('firstName', e.currentTarget.value)}
          placeholder="John"
          disabled={isSubmitting}
        />
      </div>
      <div class="form-group">
        <label for="lastName">Last Name *</label>
        <input
          type="text"
          id="lastName"
          value={lastName}
          oninput={(e) => onFieldChange('lastName', e.currentTarget.value)}
          placeholder="Smith"
          disabled={isSubmitting}
        />
      </div>
      <div class="form-group">
        <label for="email">Email *</label>
        <input
          type="email"
          id="email"
          value={email}
          oninput={(e) => onFieldChange('email', e.currentTarget.value)}
          placeholder="john@company.com"
          disabled={isSubmitting || isAuthenticated}
        />
        {#if isAuthenticated}
          <span class="field-note">Logged in as this email</span>
        {/if}
      </div>
      <div class="form-group">
        <label for="phone">Phone *</label>
        <input
          type="tel"
          id="phone"
          value={phone}
          oninput={(e) => onFieldChange('phone', e.currentTarget.value)}
          placeholder="0412 345 678"
          disabled={isSubmitting}
        />
      </div>
    </div>
  </div>

  <div class="form-section">
    <h3>Business Information</h3>
    <div class="form-grid">
      <div class="form-group">
        <label for="companyName">Company Name *</label>
        <input
          type="text"
          id="companyName"
          value={companyName}
          oninput={(e) => onFieldChange('companyName', e.currentTarget.value)}
          placeholder="Acme Logistics Pty Ltd"
          disabled={isSubmitting}
        />
      </div>
      <div class="form-group">
        <label for="abn">ABN</label>
        <input
          type="text"
          id="abn"
          value={abn}
          oninput={onABNInput}
          placeholder="12 345 678 901 (optional)"
          maxlength="14"
          disabled={isSubmitting}
          class:has-warning={abnWarning}
        />
        {#if abnWarning}
          <span class="field-warning">{abnWarning}</span>
        {/if}
      </div>
    </div>
  </div>

  <div class="form-section">
    <h3>Tell Us About Your Business <span class="section-optional">(optional)</span></h3>
    <div class="form-grid">
      <div class="form-group">
        <label for="industryType">Industry</label>
        <select
          id="industryType"
          value={industryType}
          onchange={(e) => onFieldChange('industryType', e.currentTarget.value)}
          disabled={isSubmitting}
        >
          {#each industryTypes as industry}
            <option value={industry.value}>{industry.label}</option>
          {/each}
        </select>
      </div>
      <div class="form-group">
        <label for="companySize">Company Size</label>
        <select
          id="companySize"
          value={companySize}
          onchange={(e) => onFieldChange('companySize', e.currentTarget.value)}
          disabled={isSubmitting}
        >
          {#each companySizes as size}
            <option value={size.value}>{size.label}</option>
          {/each}
        </select>
      </div>
      <div class="form-group full-width">
        <label for="referralSource">How did you hear about us?</label>
        <select
          id="referralSource"
          value={referralSource}
          onchange={(e) => onFieldChange('referralSource', e.currentTarget.value)}
          disabled={isSubmitting}
        >
          {#each referralSources as source}
            <option value={source.value}>{source.label}</option>
          {/each}
        </select>
      </div>
    </div>
  </div>

  <div class="form-actions">
    <button type="submit" class="btn-primary" disabled={isSubmitting}>
      {#if isSubmitting}
        <LoadingSpinner size="small" light />
        Creating account...
      {:else}
        Continue to Plan Selection
      {/if}
    </button>
  </div>
</form>

<style>
  .signup-form {
    background: white;
    border-radius: 16px;
    padding: clamp(1.5rem, 3vw, 2.5rem);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .form-section {
    margin-bottom: 2rem;
  }

  .form-section:last-of-type {
    margin-bottom: 1.5rem;
  }

  .form-section h3 {
    color: #1f2937;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .section-optional {
    font-weight: 400;
    font-size: 0.85rem;
    color: #9ca3af;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .form-group.full-width {
    grid-column: 1 / -1;
  }

  .form-group label {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }

  .form-group input,
  .form-group select {
    padding: 0.75rem 1rem;
    border: 1.5px solid var(--color-gray-200);
    border-radius: 10px;
    font-size: 1rem;
    background: white;
    color: var(--color-gray-800);
    transition: all 0.2s ease;
  }

  .form-group input:focus,
  .form-group select:focus {
    outline: none;
    border-color: #4A7DFF;
    box-shadow: 0 0 0 3px rgba(74, 125, 255, 0.1);
  }

  .form-group input::placeholder {
    color: var(--color-gray-400);
  }

  .form-group input:disabled,
  .form-group select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--color-gray-100);
  }

  .field-note {
    font-size: 0.75rem;
    color: var(--color-success);
    font-weight: 500;
  }

  .field-warning {
    font-size: 0.75rem;
    color: var(--color-warning);
    font-weight: 500;
  }

  .form-group input.has-warning {
    border-color: var(--color-warning);
  }

  .form-group input.has-warning:focus {
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  .form-actions {
    margin-top: 1.5rem;
  }

  .btn-primary {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.9rem 1.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    .form-grid {
      grid-template-columns: 1fr;
    }

    .form-group.full-width {
      grid-column: 1;
    }
  }
</style>
